import React, { useContext, useEffect, useMemo, useState } from "react";
import { MobXProviderContext } from "mobx-react";
import { readableIdTitle } from "../lib/utils";

// What a search-analyst answer cites (PLEX-6500), from its citations block: a chip per Plex ID,
// showing the entity's name and opening its source page (the same resolution an ID in the
// prose gets), and a chip per evidence record, opening the record's URL.

const VISIBLE_IDS = 12;
const VISIBLE_EDGES = 5;

const isWebURL = (url) => typeof url === "string" && /^https?:\/\//i.test(url);

// What kind of record an evidence chip is, from the start of its text ("eva: genetic_association
// (ENSG… -> MONDO… …)"), so records between the same two entities can be told apart (PLEX-6500).
const recordKind = (edge) => String(edge.text || "").split(" (")[0].trim();

// A long kind is shortened on the chip only. Records are compared on the whole kind, so two that
// differ past the cut still get a chip each.
const MAX_KIND = 40;
const shortKind = (kind) => (kind.length > MAX_KIND ? `${kind.slice(0, MAX_KIND - 1).trimEnd()}…` : kind);

const headingStyle = {
  fontSize: "11px",
  color: "#57534e",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  fontWeight: 600,
  margin: "2px 0 6px",
};

const rowStyle = { display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "6px" };

const chipStyle = {
  padding: "3px 9px",
  fontSize: "12px",
  color: "#374151",
  background: "#f3f4f6",
  border: "1px solid #d1d5db",
  borderRadius: "12px",
  fontFamily: "inherit",
  lineHeight: "1.5",
  maxWidth: "100%",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  textDecoration: "none",
};

const linkChipStyle = { ...chipStyle, color: "#1d4ed8", background: "#eff6ff", border: "1px solid #bfdbfe" };

const moreStyle = {
  ...chipStyle,
  cursor: "pointer",
  color: "#57534e",
  background: "transparent",
  borderStyle: "dashed",
};

const noteStyle = { fontSize: "11px", color: "#78716c", margin: "0 0 6px" };

const Chip = ({ label, href, title, testId }) => (isWebURL(href)
  ? (
    <a data-testid={testId} href={href} target="_blank" rel="noopener noreferrer" title={title} style={linkChipStyle}>
      {label}
    </a>
  )
  : <span data-testid={testId} title={title} style={chipStyle}>{label}</span>);

const MoreToggle = ({ hidden, expanded, onToggle, testId }) => {
  if (!hidden && !expanded) return null;
  return (
    <button type="button" data-testid={testId} onClick={onToggle} style={moreStyle}>
      {expanded ? "Show fewer" : `+${hidden} more`}
    </button>
  );
};

const CitationChips = ({ citations }) => {
  const { searchSessionStore } = useContext(MobXProviderContext) || {};
  const resolveSourceLinks = searchSessionStore?.resolveSourceLinks;
  const ids = useMemo(() => (Array.isArray(citations?.ids) ? citations.ids : [])
    .filter(id => typeof id === "string" && id), [citations]);
  const edges = useMemo(() => (Array.isArray(citations?.edges) ? citations.edges : [])
    .filter(edge => edge && typeof edge === "object"), [citations]);
  // Every ID a chip names: the cited ones and both ends of each evidence record, plus a record
  // URL that is a record ID rather than a web address, which resolves to its evidence page.
  const lookupIds = useMemo(() => [...new Set([...ids, ...edges.flatMap(e => [e.from, e.to]),
                                              ...edges.map(e => e.url).filter(url => !isWebURL(url))]
    .filter(id => typeof id === "string" && id))], [ids, edges]);
  const lookupKey = lookupIds.join("\n");
  const [resolved, setResolved] = useState({});
  const [allIds, setAllIds] = useState(false);
  const [allEdges, setAllEdges] = useState(false);

  useEffect(() => {
    if (!resolveSourceLinks || !lookupIds.length) return undefined;
    let cancelled = false;
    Promise.resolve(resolveSourceLinks(lookupIds))
      .then(found => { if (!cancelled) setResolved(found || {}); })
      .catch(() => {});  // unresolved IDs stay as plain chips
    return () => { cancelled = true; };
    // lookupKey stands in for lookupIds, which is a new array on each citations object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolveSourceLinks, lookupKey]);

  if (!ids.length && !edges.length) return null;

  const nameOf = (id) => (resolved[id] && !resolved[id].failed ? resolved[id].name || id : id);
  const urlOf = (id) => (resolved[id] && !resolved[id].failed ? resolved[id].url : null);
  const edgeUrl = (edge) => (isWebURL(edge.url) ? edge.url : urlOf(edge.url));
  // One chip per thing shown: two IDs naming the same record, or two copies of a record, would
  // read as duplicates.
  const distinct = (items, keyOf) => {
    const seen = new Set();
    return items.filter(item => !seen.has(keyOf(item)) && seen.add(keyOf(item)));
  };
  const idChips = distinct(ids, id => `${nameOf(id)}|${urlOf(id)}`);
  const edgeChips = distinct(edges, e => `${e.from}|${e.to}|${edgeUrl(e)}|${recordKind(e)}`);
  const shownIds = allIds ? idChips : idChips.slice(0, VISIBLE_IDS);
  const shownEdges = allEdges ? edgeChips : edgeChips.slice(0, VISIBLE_EDGES);

  return (
    <div data-testid="citation-chips" style={{
      margin: "6px 0 10px",
      padding: "10px 12px 4px",
      background: "#fafaf9",
      border: "1px solid #e7e5e4",
      borderRadius: "6px",
      fontFamily: "inherit",
    }}>
      {ids.length > 0 && (
        <>
          <div style={headingStyle}>Cited in this answer</div>
          <div style={rowStyle}>
            {shownIds.map(id => (
              <Chip key={id} testId="citation-chip-id" label={nameOf(id)} href={urlOf(id)} title={readableIdTitle(id)} />
            ))}
            <MoreToggle testId="citation-ids-more" hidden={idChips.length - shownIds.length} expanded={allIds}
                        onToggle={() => setAllIds(!allIds)} />
          </div>
          {citations.ids_omitted > 0 && (
            <div style={noteStyle}>{citations.ids_omitted} more cited IDs not listed</div>
          )}
        </>
      )}
      {edges.length > 0 && (
        <>
          <div style={headingStyle}>Evidence</div>
          <div style={rowStyle}>
            {shownEdges.map((edge, i) => (
              <Chip key={`${edge.from}|${edge.to}|${edge.url}|${i}`} testId="citation-chip-edge"
                    label={`${nameOf(edge.from)} → ${nameOf(edge.to)}${recordKind(edge) ? ` (${shortKind(recordKind(edge))})` : ""}`}
                    href={edgeUrl(edge)} title={edge.text || edge.source_id || ""} />
            ))}
            <MoreToggle testId="citation-edges-more" hidden={edgeChips.length - shownEdges.length} expanded={allEdges}
                        onToggle={() => setAllEdges(!allEdges)} />
          </div>
          {citations.edges_omitted > 0 && (
            <div style={noteStyle}>{citations.edges_omitted} more evidence records not listed</div>
          )}
        </>
      )}
    </div>
  );
};

export default CitationChips;
